import type * as React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Input } from "@/v2/ui/primitives/input";
import { Label } from "@/v2/ui/primitives/label";

export interface FormColorPickerProps {
  name: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export const FormColorPicker: React.FC<FormColorPickerProps> = ({
  name,
  label,
  required = false,
  disabled = false,
  className,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const error = errors[name];
  const errorMessage = error?.message as string | undefined;

  return (
    <div className={cn("tw-space-y-1", className)}>
      {label && (
        <Label htmlFor={name}>
          {label}
          {required && <span className="tw-text-destructive tw-ml-1">*</span>}
        </Label>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-relative tw-flex tw-items-center tw-gap-2 tw-flex-1">
              <Input
                {...field}
                id={name}
                type="text"
                placeholder="#000000"
                disabled={disabled}
                maxLength={7}
                className={cn(
                  "tw-flex-1 tw-font-mono",
                  error &&
                    "tw-border-destructive focus-visible:tw-ring-destructive"
                )}
              />
              <div className="tw-relative tw-flex tw-items-center tw-justify-center tw-h-10 tw-w-16 tw-rounded-md tw-border tw-border-input tw-bg-background tw-overflow-hidden tw-shrink-0">
                <input
                  type="color"
                  value={field.value || "#000000"}
                  onChange={(e) => field.onChange(e.target.value)}
                  disabled={disabled}
                  className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-cursor-pointer tw-opacity-0"
                />
                <div
                  className="tw-w-full tw-h-full tw-pointer-events-none"
                  style={{ backgroundColor: field.value || "#000000" }}
                />
              </div>
            </div>
          </div>
        )}
      />
      {errorMessage && (
        <p className="tw-text-sm tw-text-destructive tw-mt-1">{errorMessage}</p>
      )}
    </div>
  );
};
